import api from './api';
import type { SalesAgent, Clinic, PaginatedResponse } from '@/types';

export interface ListSalesAgentsParams {
  status?: string;
  page?: number;
  limit?: number;
}

export interface CreateSalesAgentDto {
  name: string;
  email: string;
  phone?: string;
  commissionRate?: number;
  salesCommissionRate?: number;
}

export interface UpdateSalesAgentDto {
  name?: string;
  email?: string;
  phone?: string;
  commissionRate?: number;
  salesCommissionRate?: number;
  status?: string;
}

export interface AgentClinicAssignment {
  id: string;
  clinicId: string;
  clinic: Clinic;
  assignmentType: number;
  splitPercent: number | null;
  startDate: string;
  status: string;
}

export interface AgentCommission {
  id: string;
  agentId: string;
  rentalId: string | null;
  clinicId: string | null;
  /** "rental" or "sale" */
  commissionType: string;
  shopifyOrderId: string | null;
  /** Populated for rental commissions */
  rentalAmount: number | null;
  /** Populated for sale commissions */
  orderAmount: number | null;
  commissionRate: number;
  commissionAmount: number;
  status: string;
  paidAt: string | null;
  createdAt: string;
  rental?: {
    shopifyOrderNumber: string | null;
    /** Set instead of shopifyOrderNumber on migrated orders. */
    legacyOrderId?: number | null;
  };
  clinic?: {
    name: string;
  };
}

export interface CommissionReport {
  agentId: string;
  agentName: string;
  totalRentals: number;
  totalRentalAmount: number;
  totalCommission: number;
  pendingCommission: number;
  paidCommission: number;
}

export interface SalesAgentWithStats extends SalesAgent {
  clinicCount: number;
  totalCommissions: number;
  pendingCommissions: number;
}

export const salesAgentsApi = {
  list: async (params: ListSalesAgentsParams = {}) => {
    const response = await api.get<PaginatedResponse<SalesAgentWithStats>>('/sales-agents', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<SalesAgent>(`/sales-agents/${id}`);
    return response.data;
  },

  create: async (dto: CreateSalesAgentDto) => {
    const response = await api.post<SalesAgent>('/sales-agents', dto);
    return response.data;
  },

  update: async (id: string, dto: UpdateSalesAgentDto) => {
    const response = await api.patch<SalesAgent>(`/sales-agents/${id}`, dto);
    return response.data;
  },

  getClinics: async (id: string) => {
    const response = await api.get<AgentClinicAssignment[]>(`/sales-agents/${id}/clinics`);
    return response.data;
  },

  assignToClinic: async (id: string, clinicId: string, assignmentType?: number, splitPercent?: number) => {
    const response = await api.post<AgentClinicAssignment>(`/sales-agents/${id}/clinics`, {
      clinicId,
      assignmentType,
      splitPercent,
    });
    return response.data;
  },

  removeFromClinic: async (id: string, clinicId: string) => {
    const response = await api.delete(`/sales-agents/${id}/clinics/${clinicId}`);
    return response.data;
  },

  getCommissions: async (id: string, params: { status?: string; page?: number; limit?: number } = {}) => {
    const response = await api.get<PaginatedResponse<AgentCommission>>(`/sales-agents/${id}/commissions`, { params });
    return response.data;
  },

  getCommissionReport: async (startDate: string, endDate: string) => {
    const response = await api.get<CommissionReport[]>('/sales-agents/reports/commissions', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  markCommissionsPaid: async (commissionIds: string[]) => {
    const response = await api.post('/sales-agents/commissions/mark-paid', { commissionIds });
    return response.data;
  },
};

export default salesAgentsApi;
